import {StatusCommand, type StatusCommandResponse} from '@perfectpost/perfect-post-common';
import {queryOptions, useQuery, useQueryClient} from '@tanstack/react-query';
import {useCallback} from 'react';

import {sendOrThrow} from '../../../services/sendOrThrow';
import {extensionCommandKeys} from '../keys';

/**
 * Options partagées : `useExtensionCommandStatusQuery` les monte comme requête,
 * `useFetchExtensionCommandStatus` les passe à `fetchQuery` pour lire la valeur
 * en dehors du rendu.
 */
export const extensionCommandStatusOptions = (commandId: string) =>
  queryOptions({
    queryKey: extensionCommandKeys.status(commandId),
    queryFn: (): Promise<StatusCommandResponse> => sendOrThrow(new StatusCommand(commandId)),
    // Jamais servi depuis le cache : `WorkingCommand` lit le statut dès le montage
    // (pour le nom de la commande), AVANT qu'elle soit finie — l'entrée porte alors
    // `result: undefined`. Les lectures qui suivent la fin de la commande doivent
    // relire le serveur, pas cette entrée.
    staleTime: 0,
  });

/**
 * Statut d'une commande d'extension (nom interne, résultat).
 *
 * Remplace les `perfectPostServiceClient.send(new StatusCommand(id))` dispersés
 * dans les composants qui suivent une commande (`WorkingCommand`, `Activity`,
 * `StepCheckExtension`).
 *
 * `enabled` est à passer par l'appelant : la plupart ne lisent le statut
 * qu'une fois la commande marquée `finished` par `useExtensionCommands`, sinon
 * le résultat n'y est pas encore.
 */
export function useExtensionCommandStatusQuery(commandId: string | undefined, options?: {enabled?: boolean}) {
  return useQuery({
    ...extensionCommandStatusOptions(commandId ?? ''),
    enabled: commandId !== undefined && (options?.enabled ?? true),
  });
}

/**
 * Lecture IMPÉRATIVE du statut, pour les flux qui enchaînent (« la commande est
 * finie → lire son résultat → relancer la suivante ») et qui vivent dans un
 * effet ou un `then`, pas dans le rendu. `fetchQuery` sert le cache s'il est
 * frais et déclenche la requête sinon.
 */
export function useFetchExtensionCommandStatus() {
  const queryClient = useQueryClient();

  return useCallback(
    (commandId: string): Promise<StatusCommandResponse> =>
      queryClient.fetchQuery(extensionCommandStatusOptions(commandId)),
    [queryClient],
  );
}
